import React, { useState, useEffect, useCallback } from "react";
import MainLayout from "../Themes/MainLayout";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
  Pagination,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  CalendarMonth,
  BeachAccess,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import { useCurrentUser } from "../utils/useCurrentUser";
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const HOLIDAY_TYPES = ["Full Day", "Half Day"];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const emptyForm = {
  holiday_name: "",
  holiday_date: "",
  holiday_type: "Full Day",
  description: "",
};

// ─── helpers ──────────────────────────────────────────────────────────────────
// Extract YYYY-MM-DD directly from whatever the backend sends — no timezone conversion
const toDateStr = (d) => {
  if (!d) return null;
  const s = String(d).trim();
  // Plain date "YYYY-MM-DD" — use as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // ISO timestamp "YYYY-MM-DDTHH:..." — just slice the first 10 chars, ignore time/timezone
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  return null;
};

const fmtDate = (d) => {
  const s = toDateStr(d);
  if (!s) return "—";
  const dt = new Date(s + "T00:00:00");
  return dt.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getDayName = (d) => {
  const s = toDateStr(d);
  if (!s) return "";
  return new Date(s + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
  });
};

// ─── Year selector ────────────────────────────────────────────────────────────
function YearTabs({ year, onChange }) {
  const current = new Date().getFullYear();
  const years = [current - 1, current, current + 1];
  return (
    <Stack direction="row" spacing={1}>
      {years.map((y) => (
        <Chip
          key={y}
          label={y}
          onClick={() => onChange(y)}
          color={y === year ? "primary" : "default"}
          sx={{
            fontWeight: 700,
            cursor: "pointer",
            bgcolor: y === year ? "#0052CC" : "#F4F5F7",
            color: y === year ? "#fff" : "#172B4D",
            "&:hover": { bgcolor: y === year ? "#0065FF" : "#EBECF0" },
          }}
        />
      ))}
    </Stack>
  );
}

// ─── Add / Edit Dialog ────────────────────────────────────────────────────────
function HolidayDialog({ open, onClose, onSave, initial, saving, error }) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (open) setForm(initial ? { ...initial } : { ...emptyForm });
  }, [open, initial]);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  const valid = form.holiday_name.trim() && form.holiday_date;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: "1px solid #EBECF0" }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <BeachAccess sx={{ color: "#0052CC" }} />
          <Typography fontWeight={700}>
            {initial?.holiday_id ? "Edit Holiday" : "Add Holiday"}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2} mt={0.5}>
          {error && (
            <Alert severity="error" sx={{ fontSize: "0.82rem" }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Holiday Name *"
            size="small"
            fullWidth
            value={form.holiday_name}
            onChange={set("holiday_name")}
            placeholder="e.g. Independence Day"
            InputLabelProps={{ shrink: true }}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Date *"
              type="date"
              size="small"
              fullWidth
              value={form.holiday_date}
              onChange={set("holiday_date")}
              InputLabelProps={{ shrink: true }}
              helperText={
                form.holiday_date ? getDayName(form.holiday_date) : ""
              }
            />
            <TextField
              select
              label="Type"
              size="small"
              sx={{ minWidth: 140 }}
              value={form.holiday_type}
              onChange={set("holiday_type")}
              InputLabelProps={{ shrink: true }}
            >
              {HOLIDAY_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Description (optional)"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={set("description")}
            placeholder="Any additional notes about this holiday"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, pb: 2, borderTop: "1px solid #EBECF0", pt: 1.5 }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!valid || saving}
          onClick={() => onSave(form)}
          sx={{
            textTransform: "none",
            bgcolor: "#0052CC",
            "&:hover": { bgcolor: "#0065FF" },
          }}
        >
          {saving
            ? "Saving…"
            : initial?.holiday_id
              ? "Save Changes"
              : "Add Holiday"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Holidays() {
  const { boId, roles } = useCurrentUser();

  // Admin check — same pattern as EmployeeAttendance
  const normalizedRoles = (Array.isArray(roles) ? roles : [roles || ""])
    .map(r => (r || "").toLowerCase());
  const isAdmin = normalizedRoles.includes("admin") || normalizedRoles.includes("admins");
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState("");

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    holiday: null,
  });
  const [deleting, setDeleting] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const showSnack = (msg, severity = "success") =>
    setSnack({ open: true, msg, severity });

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchHolidays = useCallback(async () => {
    if (!boId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/holidays?bo_id=${boId}&year=${year}`,
      );
      if (!res.ok) throw new Error("Failed to fetch holidays");
      const data = await res.json();
      // Sort by date ascending
      const sorted = (Array.isArray(data) ? data : data.data || []).sort(
        (a, b) => new Date(a.holiday_date) - new Date(b.holiday_date),
      );
      setHolidays(sorted);
    } catch (err) {
      console.error(err);
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  }, [boId, year]);

  useEffect(() => {
    fetchHolidays();
    setPage(1);
  }, [fetchHolidays]);

  // ── save (add / edit) ──────────────────────────────────────────────────────
  const handleSave = async (form) => {
    setSaving(true);
    setDialogError("");
    try {
      const payload = {
        bo_id: boId,
        holiday_name: form.holiday_name.trim(),
        holiday_date: form.holiday_date,
        holiday_type: form.holiday_type,
        description: form.description?.trim() || null,
      };

      if (form.holiday_id) {
        // Edit
        const res = await fetch(`${BASE_URL}/api/holidays/${form.holiday_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || "Failed to update holiday");
        }
        showSnack("Holiday updated successfully");
      } else {
        // Add
        const res = await fetch(`${BASE_URL}/api/holidays`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || "Failed to add holiday");
        }
        showSnack("Holiday added successfully");
      }

      setDialog({ open: false, initial: null });
      fetchHolidays();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    const h = deleteDialog.holiday;
    if (!h) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/holidays/${h.holiday_id}?bo_id=${boId}`,
        { method: "DELETE" },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to delete holiday");
      }
      showSnack("Holiday deleted");
      setDeleteDialog({ open: false, holiday: null });
      fetchHolidays();
    } catch (err) {
      showSnack(err.message, "error");
    } finally {
      setDeleting(false);
    }
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const filtered = holidays.filter(
    (h) =>
      h.holiday_name?.toLowerCase().includes(search.toLowerCase()) ||
      h.description?.toLowerCase().includes(search.toLowerCase()),
  );

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  // Group by month for the summary strip
  const byMonth = holidays.reduce((acc, h) => {
    const m = new Date(h.holiday_date + "T00:00:00").getMonth();
    acc[m] = (acc[m] || 0) + 1;
    return acc;
  }, {});

  return (
    <MainLayout>
      {/* ── Header ── */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="#172B4D">
            Organization Holidays
          </Typography>
          <Typography variant="caption" color="#5E6C84">
            Manage public holidays — these are automatically applied to
            timesheets and leave requests
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setDialogError("");
            setDialog({ open: true, initial: null });
          }}
          sx={{
            textTransform: "none",
            bgcolor: "#0052CC",
            "&:hover": { bgcolor: "#0065FF" },
            fontWeight: 700,
            display: isAdmin ? "inline-flex" : "none",
          }}
        >
          Add Holiday
        </Button>
      </Box>

      {/* ── Year selector + stats ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid #DFE1E6",
          borderRadius: 2,
          bgcolor: "#FAFBFC",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <YearTabs year={year} onChange={setYear} />
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              icon={<BeachAccess sx={{ fontSize: 14 }} />}
              label={`${holidays.length} holiday${holidays.length !== 1 ? "s" : ""} in ${year}`}
              size="small"
              sx={{ bgcolor: "#E9F2FF", color: "#0052CC", fontWeight: 700 }}
            />
            <Chip
              icon={<CalendarMonth sx={{ fontSize: 14 }} />}
              label={`${holidays.filter((h) => h.holiday_type === "Full Day").length} full day`}
              size="small"
              sx={{ bgcolor: "#E3FCEF", color: "#006644", fontWeight: 700 }}
            />
          </Stack>
        </Stack>

        {/* Month summary strip */}
        {holidays.length > 0 && (
          <Box sx={{ mt: 2, display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {MONTHS.map((m, i) =>
              byMonth[i] ? (
                <Tooltip
                  key={m}
                  title={`${byMonth[i]} holiday${byMonth[i] > 1 ? "s" : ""} in ${m}`}
                >
                  <Chip
                    label={`${m.slice(0, 3)} (${byMonth[i]})`}
                    size="small"
                    sx={{
                      fontSize: "0.68rem",
                      height: 20,
                      bgcolor: "#DEEBFF",
                      color: "#0052CC",
                      cursor: "default",
                    }}
                  />
                </Tooltip>
              ) : null,
            )}
          </Box>
        )}
      </Paper>

      {/* ── Search ── */}
      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Search holidays…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: "#97A0AF" }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {/* ── Table ── */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={32} sx={{ color: "#0052CC" }} />
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #DFE1E6",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#F4F5F7" }}>
                  {[
                    // "#",
                    "Actions",
                    "Holiday Name",
                    "Date",
                    "Day",
                    "Type",
                    "Description",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        color: "#5E6C84",
                        py: 1.25,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                      <BeachAccess
                        sx={{
                          fontSize: 40,
                          color: "#DFE1E6",
                          display: "block",
                          mx: "auto",
                          mb: 1,
                        }}
                      />
                      <Typography variant="body2" color="#97A0AF">
                        {search
                          ? "No holidays match your search"
                          : `No holidays added for ${year} yet`}
                      </Typography>
                      {!search && isAdmin && (
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() =>
                            setDialog({ open: true, initial: null })
                          }
                          sx={{
                            mt: 1,
                            textTransform: "none",
                            color: "#0052CC",
                          }}
                        >
                          Add the first holiday
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((h, idx) => (
                    <TableRow
                      key={h.holiday_id}
                      sx={{
                        "&:hover": { bgcolor: "#F8F9FA" },
                        "&:last-child td": { border: 0 },
                      }}
                    >
                      {/* <TableCell
                        sx={{
                          color: "#97A0AF",
                          fontSize: "0.75rem",
                          width: 40,
                        }}
                      >
                        {(page - 1) * PAGE_SIZE + idx + 1}
                      </TableCell> */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {isAdmin ? (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setDialogError("");
                                    setDialog({ open: true, initial: h });
                                  }}
                                  sx={{ p: 0.5 }}
                                >
                                  <Edit sx={{ fontSize: 15, color: "#5E6C84" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setDeleteDialog({ open: true, holiday: h })
                                  }
                                  sx={{ p: 0.5 }}
                                >
                                  <Delete sx={{ fontSize: 15, color: "#DE350B" }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Typography variant="caption" color="#C1C7D0">—</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <BeachAccess
                            sx={{ fontSize: 16, color: "#FF8B00" }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="#172B4D"
                          >
                            {h.holiday_name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="#172B4D">
                          {fmtDate(h.holiday_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="#5E6C84">
                          {getDayName(h.holiday_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={h.holiday_type || "Full Day"}
                          size="small"
                          icon={
                            h.holiday_type === "Full Day" ? (
                              <CheckCircle sx={{ fontSize: 11 }} />
                            ) : (
                              <CalendarMonth sx={{ fontSize: 11 }} />
                            )
                          }
                          sx={{
                            fontSize: "0.68rem",
                            height: 20,
                            bgcolor:
                              h.holiday_type === "Full Day"
                                ? "#E3FCEF"
                                : "#FFF0B3",
                            color:
                              h.holiday_type === "Full Day"
                                ? "#006644"
                                : "#FF8B00",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography
                          variant="caption"
                          color="#5E6C84"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h.description || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box
              display="flex"
              justifyContent="center"
              py={1.5}
              borderTop="1px solid #EBECF0"
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </Paper>
      )}

      {/* ── Add / Edit Dialog ── */}
      <HolidayDialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, initial: null })}
        onSave={handleSave}
        initial={dialog.initial}
        saving={saving}
        error={dialogError}
      />

      {/* ── Delete Confirm ── */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, holiday: null })}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>Delete Holiday?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{deleteDialog.holiday?.holiday_name}</strong> (
            {fmtDate(deleteDialog.holiday?.holiday_date)})?
          </Typography>
          <Typography
            variant="caption"
            color="#5E6C84"
            sx={{ display: "block", mt: 1 }}
          >
            This will immediately affect timesheet validations and leave day
            calculations.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, holiday: null })}
            color="inherit"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
}
